import React, { useMemo, useState } from "react";
import { fmtEuro0, MONTHS_EL_SHORT } from "@/lib/format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const UNITS = [["all", "Όλα"], ["TAF", "TAF Villas"], ["TRIBE", "TRIBE Beach"]];
const COLORS = { TAF: "#6E7A63", TRIBE: "#C86235" };
const tip = { borderRadius: 12, border: "1px solid #E3DED4", fontSize: 12 };
const axis = { fontSize: 11, fill: "#6F6A60" };
const share = (l, u) => { const t = l.taf_pct ?? 1; return u === "TAF" ? t : 1 - t; };

export const BudgetByUnit = ({ lines, lineTotal, compare, monthKeys }) => {
  const [unit, setUnit] = useState("all");
  const units = unit === "all" ? ["TAF", "TRIBE"] : [unit];
  const s = useMemo(() => {
    const tot = { TAF: 0, TRIBE: 0 }, fte = { TAF: 0, TRIBE: 0 }, roles = {};
    const monthly = monthKeys.map((m) => ({ name: MONTHS_EL_SHORT[Number(m)], TAF: 0, TRIBE: 0 }));
    lines.forEach((l) => {
      const t = lineTotal(l);
      ["TAF", "TRIBE"].forEach((u) => {
        const p = share(l, u);
        tot[u] += t * p;
        fte[u] += p;
        const r = l.position || l.department || "—";
        roles[r] = roles[r] || { name: r, TAF: 0, TRIBE: 0 };
        roles[r][u] += t * p;
        monthKeys.forEach((m, i) => { monthly[i][u] += (Number(l.months?.[m]) || 0) * p; });
      });
    });
    const bva = { TAF: { budget: 0, actual: 0 }, TRIBE: { budget: 0, actual: 0 } };
    const pct = Object.fromEntries(lines.map((l) => [l.name, l.taf_pct ?? 1]));
    (compare?.employee_rows || []).forEach((e) => {
      const t = pct[e.name] ?? 1;
      bva.TAF.budget += e.budget * t; bva.TAF.actual += e.actual * t;
      bva.TRIBE.budget += e.budget * (1 - t); bva.TRIBE.actual += e.actual * (1 - t);
    });
    return { tot, fte, roles: Object.values(roles).sort((a, b) => (b.TAF + b.TRIBE) - (a.TAF + a.TRIBE)), monthly, bva };
  }, [lines, lineTotal, compare, monthKeys]);
  const grand = s.tot.TAF + s.tot.TRIBE;
  const sel = units.reduce((a, u) => a + s.tot[u], 0);
  const worked = s.monthly.filter((m) => units.some((u) => m[u] > 0)).length || 1;
  const bvaData = units.map((u) => ({ name: u === "TAF" ? "TAF Villas" : "TRIBE Beach", budget: Math.round(s.bva[u].budget), actual: Math.round(s.bva[u].actual) }));
  const card = "rounded-2xl bg-[#F7F5F1] p-4";
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 md:p-6 space-y-6" data-testid="budget-by-unit">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-medium text-[#45423C]">Ανά επιχειρηματική μονάδα</h2>
        <div className="inline-flex rounded-xl border border-[#E3DED4] p-1 bg-[#F7F5F1]">
          {UNITS.map(([k, l]) => (
            <button key={k} data-testid={`bu-filter-${k}`} onClick={() => setUnit(k)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${unit === k ? "bg-[#45423C] text-white" : "text-[#6F6A60] hover:text-[#45423C]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className={card} data-testid="bu-kpi-total"><div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Συνολικό κόστος</div><div className="font-serif text-2xl text-[#45423C] tabular">{fmtEuro0(sel)}</div></div>
        <div className={card} data-testid="bu-kpi-month"><div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Μέσο ανά μήνα εργασίας</div><div className="font-serif text-2xl text-[#45423C] tabular">{fmtEuro0(units.reduce((a, u) => a + s.monthly.reduce((x, m) => x + m[u], 0), 0) / worked)}</div></div>
        <div className={card} data-testid="bu-kpi-fte"><div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Άτομα (ισοδύναμα)</div><div className="font-serif text-2xl text-[#45423C] tabular">{units.reduce((a, u) => a + s.fte[u], 0).toFixed(1)}</div></div>
        <div className={card} data-testid="bu-kpi-share"><div className="text-[10px] uppercase tracking-wider text-[#6F6A60]">Μερίδιο συνόλου</div><div className="font-serif text-2xl text-[#45423C] tabular">{grand ? ((sel / grand) * 100).toFixed(1) : 0}%</div>
          <div className="text-[11px] text-[#6F6A60] tabular">TAF {fmtEuro0(s.tot.TAF)} · TRIBE {fmtEuro0(s.tot.TRIBE)}</div></div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div data-testid="bu-chart-monthly">
          <h3 className="text-sm font-semibold text-[#45423C] mb-2">Μηνιαίοι μισθοί ανά μονάδα</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={s.monthly} margin={{ left: 0, right: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3DED4" vertical={false} />
              <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEuro0(v)} contentStyle={tip} cursor={{ fill: "#F7F5F1" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              {units.map((u) => <Bar key={u} dataKey={u} name={u === "TAF" ? "TAF Villas" : "TRIBE Beach"} stackId="a" fill={COLORS[u]} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="bu-chart-bva">
          <h3 className="text-sm font-semibold text-[#45423C] mb-2">Προϋπολογισμός vs Πραγματικό ({compare?.scope_label || ""})</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={bvaData} margin={{ left: 0, right: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E3DED4" vertical={false} />
              <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEuro0(v)} contentStyle={tip} cursor={{ fill: "#F7F5F1" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
              <Bar dataKey="budget" name="Προϋπολογισμός" fill="#D8D0C2" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" name="Πραγματικό" fill="#45423C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div data-testid="bu-chart-roles">
        <h3 className="text-sm font-semibold text-[#45423C] mb-2">Κόστος ανά ρόλο (ετήσιο, με δώρα/ΙΚΑ/στέγαση/σίτιση)</h3>
        <ResponsiveContainer width="100%" height={Math.max(220, s.roles.length * 30)}>
          <BarChart data={s.roles} layout="vertical" margin={{ left: 10, right: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3DED4" horizontal={false} />
            <XAxis type="number" tick={axis} axisLine={false} tickLine={false} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
            <YAxis type="category" dataKey="name" tick={axis} axisLine={false} tickLine={false} width={100} />
            <Tooltip formatter={(v) => fmtEuro0(v)} contentStyle={tip} cursor={{ fill: "#F7F5F1" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            {units.map((u) => <Bar key={u} dataKey={u} name={u === "TAF" ? "TAF Villas" : "TRIBE Beach"} stackId="r" fill={COLORS[u]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
